import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Box, Grid, MenuItem, CircularProgress, Divider } from "@mui/material";

import { useTheme } from "../ThemeContext/ThemeContext.js";
import { PrimaryButton } from "../components/StyledComponents/Buttons/AuradineButtons.js";
import {
  StyledSelect,
  StyledTextField,
  StyledFormControl,
  StyledInputLabel,
} from "../components/StyledComponents/Inputs/Inputs.js";
import {
  H7TitleLarge,
  H8TitleMedium,
  BodyLarge,
  BodyMedium,
  BodySmall,
} from "../components/StyledComponents/Typography/Typography.tsx";
import DataGrid from "../components/DataGrid/DataGrid.tsx";
import PageWrapper from "../components/PageWrapper/PageWrapper.js";
import ActionDialog from "../components/ActionDialog/ActionDialog.js";
import useAppContextProvider from "../AppContext/useAppContextProvider.js";
import * as api from "../api/api.js";

// Mirrors dbHelper.MAX_OFFBOARD_BATCH. The backend rejects anything larger; this
// only stops the operator wasting a round trip.
const MAX_SERIALS = 200;

const parseSerials = (raw) => [
  ...new Set(
    raw
      .split("\n")
      .map((s) => s.trim())
      .filter((s) => s !== "")
  ),
];

const OffboardMiners = () => {
  const { colors } = useTheme();
  const { getAccessTokenSilently } = useAuth0();
  const { setToastOpen, setToastMessage } = useAppContextProvider();

  const [orgs, setOrgs] = useState([]);
  const [orgId, setOrgId] = useState("");
  const [raw, setRaw] = useState("");
  const [resolved, setResolved] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirmText, setConfirmText] = useState("");

  const serials = useMemo(() => parseSerials(raw), [raw]);
  const overLimit = serials.length > MAX_SERIALS;
  const orgName = orgs.find((o) => o.org_id === orgId)?.name || orgId;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const data = await api.GetOffboardOrgs(token);
        if (cancelled) return;
        if (data?.status === "ok") setOrgs(data.orgs || []);
        else setError(data?.msg || "Could not load organizations");
      } catch (e) {
        // getAccessTokenSilently rejects on an expired or missing session.
        // Uncaught it becomes an unhandled rejection and the dropdown just
        // stays silently empty, with no clue why.
        if (!cancelled) setError(`Could not load organizations: ${e.message}`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getAccessTokenSilently]);

  // A successful offboard clears the textarea itself. That is a programmatic
  // change to `raw`, which would otherwise trip the invalidation effect below and
  // wipe the result we just set - so the success message never appeared.
  const keepResult = useRef(false);

  // Any change to the org or the pasted list invalidates a previous verification,
  // so the destructive button cannot act on a stale result.
  useEffect(() => {
    if (keepResult.current) {
      keepResult.current = false;
      return;
    }
    setResolved(null);
    setResult(null);
    setConfirmText("");
  }, [orgId, raw]);

  // Both handlers clear busy in a finally: getAccessTokenSilently rejects on an
  // expired session, and without this the button would spin forever with no error.
  const verify = async () => {
    setBusy(true);
    setError("");
    try {
      const token = await getAccessTokenSilently();
      const data = await api.PostResolveMiners(token, {
        org_id: orgId,
        ChassisSerialNo: serials,
      });
      if (data?.status === "ok") setResolved(data);
      else setError(data?.msg || "Verification failed");
    } catch (e) {
      setError(`Verification failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const offboard = async () => {
    setBusy(true);
    setError("");
    try {
      const token = await getAccessTokenSilently();
      const data = await api.PostOffboardMiners(token, {
        org_id: orgId,
        ChassisSerialNo: serials,
      });
      if (data?.status === "ok") {
        const n = data.offboarded?.length || 0;
        keepResult.current = true;
        setResult(data);
        setResolved(null);
        setConfirmText("");
        setRaw("");
        setToastOpen(true);
        setToastMessage(
          `Offboarded ${n} miner${n === 1 ? "" : "s"} from ${orgName}. ` +
          `This cannot be undone.`
        );
      } else {
        setError(data?.msg || "Offboard failed");
      }
    } catch (e) {
      // The request may still have reached the backend. Say so rather than
      // implying nothing happened - this call deletes records.
      setError(
        `Offboard failed: ${e.message}. Re-verify these serials before retrying, ` +
        `as the request may have partially completed.`
      );
    } finally {
      setBusy(false);
    }
  };

  const canVerify = orgId !== "" && serials.length > 0 && !overLimit && !busy;
  const matched = resolved?.matched || [];
  const confirmed = confirmText.trim() === orgName;

  // The shared DataGrid carries cell and header typography, so columns render
  // plain values and inherit the theme rather than setting their own fonts.
  const matchedColumns = useMemo(
    () => [
      { field: "ChassisSerialNo", headerName: "Chassis serial", flex: 1, minWidth: 160 },
      { field: "SerialNo", headerName: "Control board", flex: 1, minWidth: 180 },
      { field: "DGName", headerName: "Group", flex: 1, minWidth: 140 },
    ],
    []
  );

  // Tiles sit on background.highlight so they read as distinct from the dialog's
  // popover surface; 1rem radius matches the page panel.
  const Bucket = ({ label, count, tone }) => (
    <Box
      sx={{
        flex: 1,
        minWidth: 132,
        backgroundColor: colors?.background?.highlight,
        borderRadius: "1rem",
        padding: "1rem",
      }}
    >
      <H7TitleLarge sx={{ color: tone }}>{count}</H7TitleLarge>
      <BodySmall sx={{ color: colors?.text?.muted }}>{label}</BodySmall>
    </Box>
  );

  /*
   * ActionDialog renders ExtraInfoComponent with no props, so it has to close
   * over state - the same shape SiteAndGroupManagement's DeletionList uses.
   *
   * Memoised on values that do NOT change while the dialog is open. If its
   * identity changed per keystroke React would treat it as a new component type
   * and remount the subtree, so the confirm field would lose focus every
   * character. confirmText is deliberately not a dependency: the field is
   * uncontrolled and only reports upward.
   */
  const ReviewDetails = useCallback(
    () => (
      <Box display="flex" flexDirection="column" gap={4}>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Bucket
            label="will be offboarded"
            count={resolved?.matched?.length || 0}
            tone={colors?.text?.primary}
          />
          <Bucket
            label="in another org — skipped"
            count={resolved?.wrongOrg?.length || 0}
            tone={colors?.warning?.main}
          />
          <Bucket
            label="not found — skipped"
            count={resolved?.notFound?.length || 0}
            tone={colors?.text?.muted}
          />
        </Box>

        {(resolved?.matched?.length || 0) > 0 && (
          <>
            {/*
              The shared DataGrid, not StyledDataGrid: it carries the filler and
              virtual-scroller background rules, without which an empty strip of
              MUI's default white shows between the header and the first row.

              Height tracks the row count rather than being fixed, so one match
              does not leave a screen of empty grid. Header and rows are 3rem
              each, capped at five rows; beyond that it scrolls.
            */}
            <DataGrid
              colors={colors}
              height={`${3 + Math.min(resolved.matched.length, 5) * 3}rem`}
              config={{
                rows: resolved.matched,
                columns: matchedColumns,
                getRowId: (row) => row.SerialNo,
                hideFooter: true,
                disableRowSelectionOnClick: true,
              }}
            />
            {/*
              The field carries its own label, the way every other input in the
              app does (AddPriceConfigDialog passes label= on StyledTextField).
              The stacked LabelMedium + H9TitleSmall pair this replaces was not a
              pattern used anywhere else.
            */}
            <Box display="flex" flexDirection="column" gap={4}>
              <BodyMedium>
                Type {orgName} below to confirm.
              </BodyMedium>
              <StyledTextField
                colors={colors}
                margin="none"
                variant="outlined"
                label="Organization name"
                fullWidth
                defaultValue=""
                onChange={(e) => setConfirmText(e.target.value)}
              />
            </Box>
          </>
        )}
      </Box>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [resolved, orgName, colors, matchedColumns]
  );

  return (
    <PageWrapper>
      {/* Same header shape the other pages use: header-container > title-box. */}
      <Box
        className={"header-container"}
        display={"flex"}
        justifyContent="space-between"
        flexDirection="column"
        gap={4}
        marginBottom={"1.5rem"}
      >
        <Box
          className={"title-box"}
          display={"flex"}
          flexDirection={"column"}
          justifyContent={"space-between"}
          gap={3.5}
        >
          <H7TitleLarge>Offboard Miners</H7TitleLarge>
          <BodyLarge>
            Removes miners from FluxVision entirely: they are released from the
            organization and every record for them is deleted. This cannot be
            undone.
          </BodyLarge>
        </Box>
      </Box>

      {/*
        Same panel PriceConfiguration and PoolsPage use: a plain Box on
        background.paper with a 1rem radius and no border or shadow, full width
        via Grid xs={12}. StyledPaper is the wrong container here - it adds a
        border and a drop shadow that no other page surface has.
      */}
      <Grid
        container
        display={"flex"}
        flexDirection={"row"}
        spacing={4}
        sx={{ transition: "all 0.3s ease-in-out" }}
      >
        <Grid item xs={12}>
          <Box
            display={"flex"}
            flexDirection={"row"}
            backgroundColor={colors.background.paper}
            padding={"0.75rem 0 0.75rem 0"}
            borderRadius={"1rem"}
          >
            <Box
              display={"flex"}
              flexDirection={"column"}
              padding={"0 1.5rem"}
              paddingBottom={"2.5rem"}
              height={"auto"}
              minWidth={"0"}
              gap={4}
              flex={3}
              flexShrink={1}
            >
              {/*
                gap={4} is the form spacing
                AddPriceConfigDialog uses: the section heading and its field are
                siblings, not a heading with a hand-set bottom margin.
              */}

      {/* Step 1 - organization */}
      <H8TitleMedium>1. Organization</H8TitleMedium>
      <StyledFormControl colors={colors} fullWidth margin="none">
        <StyledInputLabel colors={colors} id="offboard-org-label">
          Select organization
        </StyledInputLabel>
        {/*
          minHeight, inputProps padding and MenuProps match AddPriceConfigDialog.
          MenuProps is the important one: without it the dropdown paper renders
          with MUI's default white surface instead of the app's tinted paper.
        */}
        <StyledSelect
          colors={colors}
          sx={{ minHeight: "3.5rem" }}
          variant="outlined"
          labelId="offboard-org-label"
          label="Select organization"
          fullWidth
          value={orgId}
          onChange={(e) => setOrgId(e.target.value)}
          inputProps={{ sx: { padding: "1rem 0.5rem" } }}
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: "250px",
                overflowY: "auto",
                backgroundColor: colors?.background?.paper,
                color: colors?.text?.primary,
              },
            },
          }}
        >
          {orgs.map((o) => (
            <MenuItem key={o.org_id} value={o.org_id}>
              {o.name || o.org_id}
            </MenuItem>
          ))}
        </StyledSelect>
      </StyledFormControl>

      {/* Step 2 - serials */}
      <H8TitleMedium>2. Chassis serial numbers</H8TitleMedium>
      {/*
        minRows === maxRows fixes the height, so the textarea scrolls internally
        instead of growing the page and pushing the action row out of view.
      */}
      <StyledTextField
        colors={colors}
        margin="none"
        variant="outlined"
        fullWidth
        multiline
        minRows={10}
        maxRows={10}
        placeholder={"One chassis serial per line\n000000107V\n080010018C"}
        value={raw}
        onChange={(e) => setRaw(e.target.value)}
        disabled={orgId === ""}
      />

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <BodySmall
          sx={overLimit ? { color: colors?.error?.main } : undefined}
        >
          {serials.length} of {MAX_SERIALS}
          {overLimit && ` — remove ${serials.length - MAX_SERIALS} to continue`}
        </BodySmall>
        <PrimaryButton colors={colors} onClick={verify} disabled={!canVerify}>
          {busy && !resolved ? <CircularProgress size={18} /> : "Verify serials"}
        </PrimaryButton>
      </Box>

      {error !== "" && (
        <BodyMedium sx={{ color: colors?.error?.main }}>{error}</BodyMedium>
      )}

      {/* Step 3 - review and confirm, in the app's ActionDialog */}
      <ActionDialog
        open={Boolean(resolved)}
        onClose={() => setResolved(null)}
        headerMessage={
          matched.length === 0
            ? "Nothing to offboard"
            : `Offboard ${matched.length} miner${matched.length === 1 ? "" : "s"}?`
        }
        subMessage={
          matched.length === 0
            ? `None of those chassis serials are onboarded to ${orgName}.`
            : `They will be released from ${orgName} and every record for them ` +
              `deleted. This cannot be undone.`
        }
        buttonText={
          busy
            ? "Deleting..."
            : `Delete ${matched.length} miner${matched.length === 1 ? "" : "s"}`
        }
        deleteAction={offboard}
        disabled={!confirmed || busy || matched.length === 0}
        extraInfo={true}
        ExtraInfoComponent={ReviewDetails}
        maxWidth={"md"}
        fullWidth
      />

      {/*
        Outcome. No bordered status panel: the app has no such pattern, and
        colors.success is only ever used for small icon accents elsewhere. The
        toast already announces the result; this is the durable record of it,
        rendered as plain typography on the panel surface like every other
        section.
      */}
      {result && (
        <Box display="flex" flexDirection="column" gap={2}>
          <Divider sx={{ borderColor: colors?.border?.divider }} />
          <H8TitleMedium>
            Offboarded {result.offboarded?.length || 0} miner
            {result.offboarded?.length === 1 ? "" : "s"} from {orgName}
          </H8TitleMedium>
          <BodyMedium>
            {result.wrongOrg?.length || 0} skipped as belonging to another
            organization, {result.notFound?.length || 0} not found. Their records
            are permanently deleted and cannot be recovered.
          </BodyMedium>
        </Box>
      )}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </PageWrapper>
  );
};

export default OffboardMiners;
